import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';

import useMartyContext from 'hooks/useMartyContext';
import { SmallLoader } from 'components/Loader';
import MelodyModal from 'components/common/MelodyModal';
import HtmlToReact from 'components/common/HtmlToReact';
import PrimeLogo from 'components/icons/vipDashboard/PrimeLogo';
import VipLogo from 'components/icons/vipDashboard/VipLogo';
import { markdownToHtml } from 'helpers/HtmlHelpers';
import { fetchRewardsInfo, signupForRewards } from 'actions/account/rewards';

import css from 'styles/components/landing/vipPrimeLink.scss';

const VIP_ENROLL_RETURNTO = '/c/vip-prime-link';

export const makeCopy = str => <HtmlToReact>{markdownToHtml(str)}</HtmlToReact>;

export const fallbackSlotDetails = {
  'promo': [
    { 'heading': 'Do you have Amazon Prime?', 'copy': '**Amazon Prime** members can link their Zappos account and get 2 points per $1 spent as one of the many benefits as an Amazon Prime member.' },
    { 'heading': 'Sign me up for Zappos VIP!', 'copy': '**Zappos VIP** is FREE. Sign up and get FREE next day business day shipping and other exclusive offers.' },
    { 'copy': '**Zappos VIP’s** who link their Amazon Prime accounts get 2 points per $1 spent, FREE Expedited Shipping, points redeemable for VIP Codes, and so much more!' }
  ],
  'ineligible': { 'cta': 'Got it', 'heading': 'Prime benefits eligibility', 'copy': 'Sorry your account does not qualify for Prime membership benefits. To find out what qualifies visit our our [Zappos FAQ](/c/vip-faqs).' },
  'lwa': { 'cta': 'Link my Amazon Prime', 'heading': 'Get twice the reward!' },
  'upsell': [
    { 'heading': 'You can still get VIP FREE', 'copy': 'You can still sign up for Zappos VIP for FREE and get expedited shipping, reward points for every $1 and other exclusive offers.' },
    { 'copy': '**Amazon Prime** members who sign up for Zappos VIP, get 2 points per $1 spent as one of the many benefits as an Amazon Prime member.' }
  ],
  'vip': { 'cta': 'Sign up for VIP', 'heading': 'Thanks for coming to the party!' },
  'complete': { 'cta': 'Start earning rewards', 'heading': 'You\'re all set!', 'copy': '**Nice work!** Here on forward you will be getting 2 points per $1 spent as one of the many benefits as an Amazon Prime member.' }
};

export const VipPrimeLink = ({
  slotDetails = fallbackSlotDetails,
  states = {},
  isCustomer,
  rewards,
  fetchRewardsInfo,
  signupForRewards
}) => {
  const { rewardsInfo } = rewards;

  const { marketplace: { features: { federatedLogin: { amazonAuthUrl } } } } = useMartyContext();

  useEffect(() => {
    if (!rewardsInfo) {
      fetchRewardsInfo();
    }
  }, [fetchRewardsInfo, rewardsInfo]);

  const enrollmentCheck = useRef();

  const [isEnrolling, setEnrolling] = useState(false);

  const { enrolled: vipEnrolled } = rewardsInfo || {};

  const {
    promo,
    upsell,
    ineligible,
    complete,
    vip,
    lwa
  } = slotDetails;

  const {
    isPromo = true, // on first entry
    isUpsell = false, // upsell on return from ZAP
    isIneligible = false, // on ineligible prime link
    isComplete = false, // on success

    isModal = false, // depends on where the promo will be displayed
    shouldUpsell = false // you opted out of enrolling in VIP the first time, upsell when they return
  } = states;

  const onClose = f => f;

  const onVipLwaLink = e => {
    if (!enrollmentCheck.current?.checked) {
      return true; // do the default action, which is navigate to LWA
    }

    e.preventDefault();

    // sign up before fwding them to lwa
    if (enrollmentCheck.current?.checked) {
      setEnrolling(true);

      signupForRewards(VIP_ENROLL_RETURNTO).then(() => {
        window.location = amazonAuthUrl;
      });
    }
  };

  const onVipLink = () => {
    signupForRewards(VIP_ENROLL_RETURNTO);
  };

  const getHeading = () => {
    if (isPromo) {
      return lwa.heading;
    }

    if (isUpsell) {
      return vip.heading;
    }

    if (isIneligible) {
      return ineligible.heading;
    }

    if (isComplete) {
      return complete.heading;
    }
  };

  const [ amazonContent, vipContent, guestContent ] = promo;
  const [ineligibleUpsell, upsellCopy] = upsell;

  const primeVipLogos = (
    <>
      <div className={css.logos}>
        <div className={css.primeLogo}>
          <PrimeLogo />
        </div>

        { !isIneligible &&
          <>
            <span className={css.plus} aria-label="Plus">+</span>
            <div className={css.vipLogo}>
              <VipLogo />
            </div>
          </>
        }
      </div>
    </>
  );

  const promoContent = (
    <>
      <h3>{amazonContent.heading}</h3>
      {
        isCustomer
          ? makeCopy(amazonContent.copy)
          : makeCopy(guestContent.copy)
      }

      {
        isEnrolling && <SmallLoader />
      }

      {
        (!isEnrolling && isCustomer && !vipEnrolled) && // if they're a recognized customer, and they're not already part of VIP
          <>
            <input
              ref={enrollmentCheck}
              defaultChecked={true}
              id="signUpVIP"
              type="checkbox" />
            <label htmlFor="signUpVIP">{vipContent.heading}</label>
            { makeCopy(vipContent.copy) }
          </>
      }

      <div className={css.actions}>
        { isModal && <button className={css.cancel} type="button" onClick={onClose}>Cancel</button> }
        { lwa.cta && <Link className={css.primary} to={amazonAuthUrl} onClick={onVipLwaLink}>{lwa.cta}</Link> }
      </div>
    </>
  );

  const upsellContent = (
    <>
      { makeCopy(upsellCopy.copy) }

      <div className={css.actions}>
        { isModal && <button className={css.cancel} type="button" onClick={onClose}>Cancel</button> }
        { vip.cta && <button className={css.primary} onClick={onVipLink} type="button">{vip.cta}</button> }
      </div>
    </>
  );

  const ineligibleContent = (
    <>
      { makeCopy(ineligible.copy) }

      {
        shouldUpsell && // if `isIneligiblePrime` and `notVIP`
          <>
            <h3>{ineligibleUpsell.heading}</h3>
            { makeCopy(ineligibleUpsell.copy) }
          </>
      }

      <div className={css.actions}>
        { (shouldUpsell && isModal) && <button className={css.cancel} type="button" onClick={onClose}>Cancel</button> }

        {
          shouldUpsell
            ? vip.cta && <button className={css.primary} onClick={onVipLink} type="button">{vip.cta}</button>
            : ineligible.cta && <button className={css.primary} type="button">{ineligible.cta}</button>
        }
      </div>
    </>
  );

  const completeContent = (
    <>
      { makeCopy(complete.copy) }
      <div className={css.actions}>
        { complete.cta && <button className={css.primary} type="button" onClick={onClose}>{complete.cta}</button> }
      </div>
    </>
  );

  const makeSteps = () => (
    <div className={css.step}>
      { primeVipLogos }
      { isPromo && promoContent /* intro, unrecognized customer */ }
      { isUpsell && upsellContent /* linked, upsell on return */ }
      { isIneligible && ineligibleContent /* linked, ineligible, not signed up for vip */ }
      { isComplete && completeContent /* linked, eligible, already vip */ }
    </div>
  );

  if (isModal) {
    return (
      <MelodyModal
        buttonTestId="close"
        className={cn({ [css.modal]: isModal })}
        contentLabel="VIP Prime Linking"
        heading={getHeading()}
        bodyOpenClassName={css.modalOpenBodyStyle}
        onRequestClose={onClose}
        isOpen={true}>

        { makeSteps() }
      </MelodyModal>
    );
  }

  return (
    <div className={css.page}>
      <h2>{ getHeading() }</h2>
      { makeSteps() }
    </div>
  );
};

export const mapStateToProps = state => {
  const { cookies, rewards } = state;
  const isCustomer = 'x-main' in cookies;

  return {
    isCustomer,
    rewards
  };
};

export default connect(mapStateToProps, {
  fetchRewardsInfo,
  signupForRewards
})(VipPrimeLink);
